import { Button } from "@/components/core/buttons/button";
import Combobox from "@/components/core/forms/combobox.tsx";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import ImageSelect from "@/components/core/forms/image-select";
import { Input } from "@/components/core/forms/input";
import { RadioGroup, RadioGroupItem } from "@/components/core/forms/radio-group.tsx";
import Select from "@/components/core/forms/select.tsx";
import { useRemoveAvatarMutation } from "@/features/dashboard/api/queries/profile-queries.ts";
import provinceService from "@/features/dashboard/api/services/province-service.ts";
import regionService from "@/features/dashboard/api/services/region-service.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import cityService from "../api/services/city-service";
import { type UserFormSchema, userFormSchema } from "../types/user";

export default function UserProfileDetailsForm({
	data,
	onSubmit,
}: {
	data?: UserFormSchema;
	onSubmit: (data: UserFormSchema, reset: () => void) => void;
}) {
	const removeAvatarMutation = useRemoveAvatarMutation();
	const form = useForm<UserFormSchema>({
		resolver: zodResolver(userFormSchema),
		defaultValues: {
			id: data?.id,
			first_name: data?.first_name || "",
			middle_name: data?.middle_name || "",
			last_name: data?.last_name || "",
			suffix: data?.suffix || "",
			email: data?.email || "",
			contact_number: data?.contact_number || "",
			sex: data?.sex || "",
			birthdate: data?.birthdate || "",
			preferred_gender: data?.preferred_gender || "",
			zipcode: data?.zipcode || "",
			city_code: data?.city?.name || "",
			barangay: data?.barangay || "",
			address_1: data?.address_1 || "",
			address_2: data?.address_2 || "",
			preferred_middle_initials: data?.preferred_middle_initials || "",
			region_code: data?.region?.name || "",
			province_code: data?.province?.name || "",
			post_as_judge: data?.post_as_judge || false,
			post_as_multiple_role: data?.post_as_multiple_role || false,
		},
	});
	const regionCode = form.watch("region_code");
	const provinceCode = form.watch("province_code");

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">Profile Picture</span>
							<span className="text-base text-foreground/70">Use a clear, high-quality image. Make sure your face is visible, avoid group photos.</span>
						</div>
					</div>
					<div className="flex flex-col w-full gap-4">
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-12 flex flex-col w-full mb-4">
								<Controller
									render={({ field }) => (
										<ImageSelect
											initials={data?.initials || ""}
											avatar={data?.avatar_thumbnail}
											onImageRemove={async () => {
												await removeAvatarMutation.mutateAsync();

												field.onChange("");
											}}
											onCrop={(avatar) => {
												field.onChange(avatar);
											}}
										/>
									)}
									control={form.control}
									name="avatar"
								/>
							</div>
							{/* <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>First Name</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Last Name</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="middle_name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Middle Name</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-3 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="preferred_middle_initials"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Middle Initial</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-3 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="suffix"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Suffix</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Email Address</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="Email Address" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="contact_number"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Contact number</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input
                          autoComplete="off"
                          {...field}
                          onChange={(e) => {
                            form.trigger("contact_number");
                            const onlyNumbers = e.target.value.replace(/(?!^\+)[^\d]/g, "");
                            field.onChange(onlyNumbers);
                          }}
                        />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-12 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="sex"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Sex Assigned at Birth</FormLabel>
                      </div>
                      <RadioGroup disabled onValueChange={field.onChange} defaultValue={field.value}>
                        <div className="flex flex-row w-auto gap-2">
                          <FormControl>
                            <RadioGroupItem value="Male" />
                          </FormControl>
                          <FormLabel>
                            <div className="flex flex-col w-full">
                              <span className="text-base text-foreground">Male</span>
                            </div>
                          </FormLabel>
                        </div>
                        <div className="flex flex-row w-auto gap-2">
                          <FormControl>
                            <RadioGroupItem value="Female" />
                          </FormControl>
                          <FormLabel>
                            <div className="flex flex-col w-full">
                              <span className="text-base text-foreground">Female</span>
                            </div>
                          </FormLabel>
                        </div>
                      </RadioGroup>
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="birthdate"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Birthdate</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input type="date" autoComplete="off" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-6 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="preferred_gender"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Preferred Gender Pronouns, if any</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup>
                        <Select
                          disabled
                          placeholder="Select Preferred Gender"
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          options={[
                            {
                              value: "He/Him",
                              label: "He/Him",
                            },
                            {
                              value: "She/Her",
                              label: "She/Her",
                            },
                            {
                              value: "They/Them",
                              label: "They/Them",
                            },
                          ]}
                        />
                      </FormGroup>
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-12 flex items-center text-[14px] border-b pb-2 text-gray-500 font-medium">Address</div>
              <div className="col-span-4 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="region_code"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Region</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup>
                        <Combobox
                          disabled
                          defaultValue={field.value}
                          onValueChange={(value) => {
                            form.setValue("province_code", "");
                            form.setValue("city_code", "");

                            field.onChange(value);
                          }}
                          placeholderValue={{
                            label: data?.region?.name,
                            value: data?.region?.code,
                          }}
                          loadOptions={async (value: string) => {
                            const items = await regionService.list({
                              "filter[search]": value,
                            });

                            return items.data.map((item) => ({
                              label: item.name,
                              value: item.code,
                            }));
                          }}
                        />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-4 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="province_code"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Province</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup>
                        <Combobox
                          defaultValue={field.value}
                          disabled={!regionCode}
                          key={regionCode}
                          onValueChange={(value) => {
                            form.setValue("city_code", "");

                            field.onChange(value);
                          }}
                          placeholderValue={{
                            label: data?.province?.name,
                            value: data?.province?.code,
                          }}
                          loadOptions={async (value: string) => {
                            const items = await provinceService.list({
                              "filter[search]": value,
                              "filter[region_code]": regionCode,
                            });

                            return items.data.map((item) => ({
                              label: item.name,
                              value: item.code,
                            }));
                          }}
                        />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-4 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="city_code"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>City</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup>
                        <Combobox
                          defaultValue={field.value}
                          disabled={!provinceCode}
                          key={provinceCode}
                          onValueChange={field.onChange}
                          placeholderValue={{
                            label: data?.city?.name,
                            value: data?.city?.code,
                          }}
                          loadOptions={async (value: string) => {
                            const items = await cityService.list({
                              "filter[search]": value,
                              "filter[province_code]": provinceCode,
                            });

                            return items.data.map((item) => ({
                              label: item.name,
                              value: item.code,
                            }));
                          }}
                        />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-4 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="barangay"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Barangay</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="Barangay" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-8 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="address_1"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel required>Street/Lot No./Blding/Room No.</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="Street/Lot No./Blding/Room No." {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-8 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="address_2"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Apartment/Suite/Unit</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup iconPosition="left">
                        <Input autoComplete="off" placeholder="Apartment/Suite/Unit" {...field} />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-4 w-full">
                <FormField
                  disabled
                  control={form.control}
                  name="zipcode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip Code</FormLabel>
                      <FormGroup iconPosition="left">
                        <NumericFormat
                          decimalScale={2}
                          thousandsGroupStyle="thousand"
                          thousandSeparator={false}
                          maxLength={4}
                          className={clsx(
                            "block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
                            "disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
                          )}
                          {...field}
                          onValueChange={(values) => {
                            if (values.value.length !== 4) {
                              form.trigger("zipcode");
                            }
                            field.onChange(values.value);
                          }}
                        />
                      </FormGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-6 w-full">
                <FormField
                  control={form.control}
                  name="post_as_judge"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Post As Judge</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup>
                        <Select
                          placeholder="Select Preferred Gender"
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          options={[
                            {
                              value: true,
                              label: "Yes",
                            },
                            {
                              value: false,
                              label: "No",
                            },
                          ]}
                        />
                      </FormGroup>
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-6 w-full">
                <FormField
                  control={form.control}
                  name="post_as_multiple_role"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-col w-full">
                        <FormLabel>Post As Multiple Role</FormLabel>
                        <FormDescription />
                      </div>
                      <FormGroup>
                        <Select
                          placeholder="Select Preferred Gender"
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          options={[
                            {
                              value: true,
                              label: "Yes",
                            },
                            {
                              value: false,
                              label: "No",
                            },
                          ]}
                        />
                      </FormGroup>
                    </FormItem>
                  )}
                />
              </div> */}
						</div>
						<div className="flex flex-row w-full mt-2">
							<Button type="submit">Save Changes</Button>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
